import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import { baseurladdress } from "../baseurl";

const STATUS_COLORS = {
  completed: { bg: "#dcfce7", fg: "#166534", border: "#86efac" },
  pending: { bg: "#fef3c7", fg: "#92400e", border: "#fcd34d" },
  rejected: { bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" },
  cancelled: { bg: "#e2e8f0", fg: "#334155", border: "#cbd5e1" },
  approved: { bg: "#dbeafe", fg: "#1d4ed8", border: "#93c5fd" },
  overdue: { bg: "#fecaca", fg: "#991b1b", border: "#fca5a5" },
};

const GROUP_LABELS = {
  hour: "Hour",
  day: "Day",
  week: "Week",
  month: "Month",
  staff: "Staff",
  location: "Location",
};

const GROUP_BY_OPTIONS = ["hour", "day", "week", "month", "staff", "location"];
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const toDateInput = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTimeRange = (from, to) => {
  if (!from && !to) return "-";
  if (!from) return to;
  if (!to) return from;
  return `${from} - ${to}`;
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  return [];
};

const numericValue = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const statusBadgeStyle = (value) => {
  const key = String(value || "").toLowerCase();
  const theme = STATUS_COLORS[key] || STATUS_COLORS.pending;
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    color: theme.fg,
    background: theme.bg,
    border: `1px solid ${theme.border}`,
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  };
};

const summarizeFilters = (filters, staffLabel) => [
  { label: "Date range", value: `${formatDate(filters.from_date)} to ${formatDate(filters.to_date)}` },
  { label: "Staff", value: staffLabel },
  { label: "Group by", value: GROUP_LABELS[filters.group_by] || filters.group_by },
  { label: "Status", value: filters.status || "All" },
  { label: "Approval", value: filters.approval_status || "All" },
];

function buildQueryParams(filters, selectedStaffId, details = false) {
  const params = new URLSearchParams();
  params.set("from_date", filters.from_date);
  params.set("to_date", filters.to_date);
  params.set("group_by", filters.group_by);

  if (selectedStaffId && selectedStaffId !== "all") {
    params.set("staff_id", selectedStaffId);
  }

  if (filters.client_id) params.set("client_id", filters.client_id);
  if (filters.location_id) params.set("location_id", filters.location_id);
  if (filters.status) params.set("status", filters.status);
  if (filters.approval_status) params.set("approval_status", filters.approval_status);

  if (details) {
    params.set("page", String(filters.page));
    params.set("page_size", String(filters.page_size));
    params.set("sort_by", filters.sort_by);
    params.set("sort_order", filters.sort_order);
  }

  return params.toString();
}

function resolveStaffLabel(staffList, selectedStaffId) {
  if (!selectedStaffId || selectedStaffId === "all") return "All Staff";
  const staff = staffList.find((item) => String(item.staff_id) === String(selectedStaffId));
  return staff ? `${staff.staff_name}${staff.staff_code ? ` (${staff.staff_code})` : ""}` : `Staff ${selectedStaffId}`;
}

function emptySummary() {
  return {
    total_appointments: 0,
    completed: 0,
    pending: 0,
    rejected: 0,
    cancelled: 0,
    completion_rate: 0,
    rejection_rate: 0,
    followups_total: 0,
    followups_due: 0,
    followups_overdue: 0,
    reports_completed: 0,
    reports_pending: 0,
  };
}

function AppointmentReport() {
  const token = localStorage.getItem("token");

  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const [staffList, setStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [filters, setFilters] = useState({
    from_date: toDateInput(monthStart),
    to_date: toDateInput(today),
    group_by: "week",
    status: "",
    approval_status: "",
    client_id: "",
    location_id: "",
    page: 1,
    page_size: 50,
    sort_by: "appointment_date",
    sort_order: "desc",
  });
  const [selectedStaffId, setSelectedStaffId] = useState("all");
  const [reportData, setReportData] = useState(null);
  const [detailsData, setDetailsData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const api = useMemo(
    () =>
      axios.create({
        baseURL: baseurladdress,
        headers: { Authorization: `Bearer ${token}` },
      }),
    [token]
  );

  const staffLabel = useMemo(() => resolveStaffLabel(staffList, selectedStaffId), [staffList, selectedStaffId]);
  const summary = reportData?.summary || emptySummary();
  const reportFilters = reportData?.filters || {};
  const detailsFilters = detailsData?.filters || {};
  const isAllStaff = selectedStaffId === "all";

  useEffect(() => {
    const loadStaff = async () => {
      setStaffLoading(true);
      try {
        const res = await axios.get(baseurladdress + "staff/findallstaff", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data?.status === "Success" ? res.data.staff || [] : res.data?.staff || [];
        setStaffList(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("findallstaff error:", err);
        setStaffList([]);
      } finally {
        setStaffLoading(false);
      }
    };

    if (token) {
      loadStaff();
    }
  }, [token]);

  const loadReports = async (nextPage = filters.page) => {
    if (!token) {
      setError("Please sign in again to view this report.");
      return;
    }

    setLoading(true);
    setError("");

    const nextFilters = { ...filters, page: nextPage };
    try {
      const [mainRes, detailsRes] = await Promise.all([
        api.get(`api/reports/appointments?${buildQueryParams(nextFilters, selectedStaffId, false)}`),
        api.get(`api/reports/appointments/details?${buildQueryParams(nextFilters, selectedStaffId, true)}`),
      ]);

      setReportData(mainRes.data || null);
      setDetailsData(detailsRes.data || null);
      setPagination(detailsRes.data?.pagination || { page: 1, page_size: nextFilters.page_size, total: 0, total_pages: 0 });
      setFilters((current) => ({ ...current, page: nextPage }));
    } catch (err) {
      console.error("appointment report error:", err);
      setError(err.response?.data?.message || "Unable to load appointment report.");
      setReportData(null);
      setDetailsData([]);
      setPagination({ page: 1, page_size: filters.page_size, total: 0, total_pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadReports(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const trendRows = normalizeArray(reportData?.trend);
  const staffPerformanceRows = normalizeArray(reportData?.staff_performance);
  const coverageData = reportData?.coverage || {};
  const followupsData = reportData?.followups || {};
  const exceptionsData = reportData?.exceptions || {};

  const totalPages = pagination.total_pages || 1;
  const currentPage = pagination.page || 1;

  const visibleMetrics = [
    {
      title: "Total appointments",
      value: numericValue(summary.total_appointments).toLocaleString(),
      accent: "#0f766e",
      note: "All appointments matching the current filters",
    },
    {
      title: "Completed",
      value: numericValue(summary.completed).toLocaleString(),
      accent: "#166534",
      note: `${numericValue(summary.completion_rate).toFixed(2)}% completion rate`,
    },
    {
      title: "Pending",
      value: numericValue(summary.pending).toLocaleString(),
      accent: "#92400e",
      note: "Appointments still waiting on completion",
    },
    {
      title: "Rejected",
      value: numericValue(summary.rejected).toLocaleString(),
      accent: "#991b1b",
      note: `${numericValue(summary.rejection_rate).toFixed(2)}% rejection rate`,
    },
    {
      title: "Follow-ups due",
      value: numericValue(summary.followups_due).toLocaleString(),
      accent: "#1d4ed8",
      note: `${numericValue(summary.followups_total).toLocaleString()} total follow-ups`,
    },
    {
      title: "Reports pending",
      value: numericValue(summary.reports_pending).toLocaleString(),
      accent: "#7c3aed",
      note: `${numericValue(summary.reports_completed).toLocaleString()} reports completed`,
    },
  ];

  const exportPdf = async () => {
    if (!reportData || !detailsData) return;

    setExporting(true);
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const left = 14;

      const addHeader = () => {
        doc.setFillColor(15, 118, 110);
        doc.rect(0, 0, pageWidth, 24, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text("Appointment Report", left, 15);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - left, 15, { align: "right" });
      };

      const addFooter = (pageNumber) => {
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`Page ${pageNumber}`, pageWidth - left, pageHeight - 8, { align: "right" });
      };

      addHeader();

      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text(isAllStaff ? "All Staff Summary" : "Single Staff Summary", left, 34);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Staff: ${staffLabel}`, left, 41);
      doc.text(`Period: ${formatDate(filters.from_date)} to ${formatDate(filters.to_date)}`, left, 47);
      doc.text(`Group By: ${GROUP_LABELS[filters.group_by] || filters.group_by}`, left, 53);
      doc.text(`Status: ${filters.status || "All"}`, 110, 41);
      doc.text(`Approval: ${filters.approval_status || "All"}`, 110, 47);
      doc.text(`Sort: ${filters.sort_by} (${filters.sort_order})`, 110, 53);

      let y = 64;
      const metricPairs = [
        ["Total appointments", summary.total_appointments],
        ["Completed", summary.completed],
        ["Pending", summary.pending],
        ["Rejected", summary.rejected],
        ["Cancelled", summary.cancelled],
        ["Completion rate", `${numericValue(summary.completion_rate).toFixed(2)}%`],
      ];

      autoTable(doc, {
        startY: y,
        body: metricPairs.map(([label, value]) => [label, String(value)]),
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 2.5 },
        head: [["Metric", "Value"]],
        headStyles: { fillColor: [15, 118, 110] },
        margin: { left: left, right: left },
      });

      y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : y + 28;

      if (isAllStaff && staffPerformanceRows.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text("Staff Performance", left, y);
        y += 4;

        autoTable(doc, {
          startY: y,
          head: [["Staff", "Total", "Completed", "Pending", "Rejected", "Completion %"]],
          body: staffPerformanceRows.map((row) => [
            row.staff_name || row.name || row.staff || `Staff ${row.staff_id ?? ""}`,
            String(row.total_appointments ?? row.total ?? row.count ?? 0),
            String(row.completed ?? 0),
            String(row.pending ?? 0),
            String(row.rejected ?? 0),
            `${numericValue(row.completion_rate ?? row.rate ?? 0).toFixed(2)}%`,
          ]),
          theme: "striped",
          styles: { fontSize: 8.5, cellPadding: 2.2 },
          headStyles: { fillColor: [30, 41, 59] },
          margin: { left: left, right: left },
        });

        y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : y + 20;
      } else if (!isAllStaff) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text("Selected Staff", left, y);
        y += 4;

        autoTable(doc, {
          startY: y,
          body: [[staffLabel, String(selectedStaffId)]],
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 2.5 },
          head: [["Staff Name", "Staff ID"]],
          headStyles: { fillColor: [30, 41, 59] },
          margin: { left: left, right: left },
        });

        y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : y + 20;
      }

      if (trendRows.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text("Trend", left, y);
        y += 4;

        autoTable(doc, {
          startY: y,
          head: [["Period", "Total", "Completed", "Pending", "Rejected"]],
          body: trendRows.map((row) => [
            String(row.period ?? row.label ?? row.date ?? row.day ?? row.week ?? "-"),
            String(row.total_appointments ?? row.total ?? 0),
            String(row.completed ?? 0),
            String(row.pending ?? 0),
            String(row.rejected ?? 0),
          ]),
          theme: "striped",
          styles: { fontSize: 8.5, cellPadding: 2.2 },
          headStyles: { fillColor: [15, 118, 110] },
          margin: { left: left, right: left },
        });

        y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : y + 20;
      }

      const detailRows = normalizeArray(detailsData?.data);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text("Appointment Details", left, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        head: [["ID", "Date", "Staff", "Client", "Location", "Status", "Approval", "Time", "Report"]],
        body: detailRows.map((row) => [
          String(row.appointment_id ?? row.appoint_id ?? "-"),
          formatDate(row.appointment_date ?? row.appoint_appointmentdate),
          row.staff?.name ?? row.appoint_staffname ?? row.staff_name ?? "-",
          row.client?.name ?? row.appoint_clientname ?? "-",
          row.location?.name ?? row.appoint_locationname ?? "-",
          String(row.status ?? row.appoint_status ?? "-"),
          String(row.approval_status ?? row.appointapproval_status ?? "-"),
          formatTimeRange(row.time_from ?? row.appoint_timefrom, row.time_to ?? row.appoint_timeto),
          String(row.report_status ?? row.report_state ?? "-"),
        ]),
        theme: "striped",
        styles: { fontSize: 7.3, cellPadding: 1.8 },
        headStyles: { fillColor: [30, 41, 59] },
        margin: { left: left, right: left },
      });

      const followupRows = Object.entries(followupsData || {});
      const exceptionRows = Object.entries(exceptionsData || {});
      const hasAppendix = followupRows.length > 0 || exceptionRows.length > 0;
      if (hasAppendix) {
        doc.addPage();
        addHeader();
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("Summary Notes", left, 34);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        const notes = [
          `Follow-ups total: ${numericValue(summary.followups_total)}`,
          `Follow-ups due: ${numericValue(summary.followups_due)}`,
          `Follow-ups overdue: ${numericValue(summary.followups_overdue)}`,
          `Reports completed: ${numericValue(summary.reports_completed)}`,
          `Reports pending: ${numericValue(summary.reports_pending)}`,
        ];
        notes.forEach((line, index) => {
          doc.text(`• ${line}`, left, 44 + index * 6);
        });

        if (followupRows.length > 0) {
          autoTable(doc, {
            startY: 80,
            head: [["Follow-up key", "Value"]],
            body: followupRows.map(([key, value]) => [key, typeof value === "object" ? JSON.stringify(value) : String(value)]),
            theme: "grid",
            styles: { fontSize: 8.5, cellPadding: 2.2 },
            headStyles: { fillColor: [15, 118, 110] },
            margin: { left: left, right: left },
          });
        }

        if (exceptionRows.length > 0) {
          autoTable(doc, {
            startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : 80,
            head: [["Exception key", "Value"]],
            body: exceptionRows.map(([key, value]) => [key, typeof value === "object" ? JSON.stringify(value) : String(value)]),
            theme: "grid",
            styles: { fontSize: 8.5, cellPadding: 2.2 },
            headStyles: { fillColor: [153, 27, 27] },
            margin: { left: left, right: left },
          });
        }
      }

      const totalPages = doc.internal.getNumberOfPages();
      for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page);
        addFooter(page);
      }

      const fileName = `appointment_report_${isAllStaff ? "all_staff" : String(selectedStaffId).replace(/\s+/g, "_")}_${filters.from_date}_to_${filters.to_date}.pdf`;
      doc.save(fileName);
    } finally {
      setExporting(false);
    }
  };

  const summaryCards = useMemo(() => visibleMetrics, [summary]);

  return (
    <div
      style={{
        padding: 24,
        background: "linear-gradient(180deg, #f8fafc 0%, #eef6f4 100%)",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          maxWidth: 1440,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <Card
          style={{
            border: "none",
            borderRadius: 20,
            overflow: "hidden",
            background: "linear-gradient(135deg, #0f766e 0%, #115e59 55%, #134e4a 100%)",
            color: "white",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)",
          }}
        >
          <Card.Body style={{ padding: 24 }}>
            <Row className="g-3 align-items-center">
              <Col lg={8}>
                <div style={{ opacity: 0.82, fontSize: 12, textTransform: "uppercase", letterSpacing: 1.5 }}>
                  Appointment analytics
                </div>
                <h2 style={{ margin: "8px 0 10px", fontWeight: 800 }}>Appointment Report</h2>
                <div style={{ maxWidth: 760, fontSize: 14, lineHeight: 1.7, opacity: 0.95 }}>
                  Filter appointments by date, status, approval state, and staff. The page shows a live web
                  report and can export the same view as a PDF for a single staff member or for all staff.
                </div>
              </Col>
              <Col lg={4}>
                <div
                  style={{
                    borderRadius: 18,
                    padding: 16,
                    background: "rgba(255,255,255,0.12)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.18)",
                  }}
                >
                  <div style={{ fontSize: 12, opacity: 0.85, textTransform: "uppercase", letterSpacing: 1 }}>
                    Current view
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6 }}>{staffLabel}</div>
                  <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
                    {formatDate(filters.from_date)} to {formatDate(filters.to_date)}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Badge bg="light" text="dark" style={{ borderRadius: 999, padding: "6px 10px" }}>
                      {GROUP_LABELS[filters.group_by] || filters.group_by}
                    </Badge>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)" }}>
          <Card.Body style={{ padding: 20 }}>
            <Row className="g-3">
              <Col md={4} lg={2}>
                <Form.Label>From date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => setFilters((current) => ({ ...current, from_date: e.target.value }))}
                />
              </Col>
              <Col md={4} lg={2}>
                <Form.Label>To date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => setFilters((current) => ({ ...current, to_date: e.target.value }))}
                />
              </Col>
              <Col md={4} lg={2}>
                <Form.Label>Group by</Form.Label>
                <Form.Select
                  value={filters.group_by}
                  onChange={(e) => setFilters((current) => ({ ...current, group_by: e.target.value }))}
                >
                  {GROUP_BY_OPTIONS.map((item) => (
                    <option key={item} value={item}>
                      {GROUP_LABELS[item]}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4} lg={2}>
                <Form.Label>Appointment status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))}
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Col>
              <Col md={4} lg={2}>
                <Form.Label>Approval status</Form.Label>
                <Form.Select
                  value={filters.approval_status}
                  onChange={(e) => setFilters((current) => ({ ...current, approval_status: e.target.value }))}
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </Form.Select>
              </Col>
              <Col md={4} lg={2}>
                <Form.Label>Staff scope</Form.Label>
                <Form.Select value={selectedStaffId} onChange={(e) => setSelectedStaffId(e.target.value)}>
                  <option value="all">All Staff</option>
                  {staffLoading && <option>Loading staff...</option>}
                  {!staffLoading &&
                    staffList.map((staff) => (
                      <option key={staff.staff_id} value={String(staff.staff_id)}>
                        {staff.staff_name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className="g-3 mt-1 align-items-end">
              <Col md={3} lg={2}>
                <Form.Label>Page size</Form.Label>
                <Form.Select
                  value={filters.page_size}
                  onChange={(e) =>
                    setFilters((current) => ({ ...current, page_size: Number(e.target.value), page: 1 }))
                  }
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3} lg={2}>
                <Form.Label>Sort order</Form.Label>
                <Form.Select
                  value={filters.sort_order}
                  onChange={(e) => setFilters((current) => ({ ...current, sort_order: e.target.value }))}
                >
                  <option value="desc">Newest first</option>
                  <option value="asc">Oldest first</option>
                </Form.Select>
              </Col>
              <Col md={6} lg={8} className="d-flex gap-2 justify-content-md-end flex-wrap">
                <Button
                  variant="outline-secondary"
                  onClick={() =>
                    setFilters((current) => ({
                      ...current,
                      status: "",
                      approval_status: "",
                      client_id: "",
                      location_id: "",
                    }))
                  }
                >
                  Clear extra filters
                </Button>
                <Button variant="success" onClick={() => loadReports(1)} disabled={loading || exporting}>
                  {loading ? "Loading..." : "Load report"}
                </Button>
                <Button
                  variant="primary"
                  onClick={exportPdf}
                  disabled={loading || exporting || !reportData}
                  style={{ background: "#0f766e", borderColor: "#0f766e" }}
                >
                  {exporting ? "Preparing PDF..." : "Download PDF"}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="g-3">
          {summaryCards.map((metric) => (
            <Col key={metric.title} xs={12} sm={6} xl={4}>
              <Card
                style={{
                  border: "1px solid #dbe5e3",
                  borderRadius: 18,
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
                  height: "100%",
                }}
              >
                <Card.Body>
                  <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
                    {metric.title}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 30, fontWeight: 800, color: metric.accent }}>
                    {metric.value}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                    {metric.note}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Card style={{ border: "1px solid #dbe5e3", borderRadius: 18, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)" }}>
          <Card.Body style={{ padding: 20 }}>
            <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
              <div>
                <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
                  Web view
                </div>
                <h5 style={{ margin: "4px 0 0", fontWeight: 800 }}>
                  {isAllStaff ? "Entire staff report" : "Single staff report"}
                </h5>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Badge bg="light" text="dark" style={{ borderRadius: 999, padding: "8px 10px" }}>
                  {reportFilters.group_by ? `Grouped by ${GROUP_LABELS[reportFilters.group_by] || reportFilters.group_by}` : "Grouped view"}
                </Badge>
                <Badge bg="light" text="dark" style={{ borderRadius: 999, padding: "8px 10px" }}>
                  {detailsFilters.page_size ? `${detailsFilters.page_size} rows/page` : "50 rows/page"}
                </Badge>
              </div>
            </div>

            <div className="mt-3">
              <Row className="g-3">
                {summarizeFilters(filters, staffLabel).map((item) => (
                  <Col key={item.label} md={6} xl={4}>
                    <div
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 14,
                        padding: 12,
                        background: "#f8fafc",
                        height: "100%",
                      }}
                    >
                      <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>
                        {item.label}
                      </div>
                      <div style={{ marginTop: 6, fontWeight: 700, color: "#0f172a" }}>{item.value}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            <Row className="g-3 mt-1">
              <Col xl={6}>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 16, overflow: "hidden" }}>
                  <div
                    style={{
                      padding: "12px 14px",
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                      fontWeight: 700,
                    }}
                  >
                    Trend
                  </div>
                  <Table responsive bordered hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Total</th>
                        <th>Completed</th>
                        <th>Pending</th>
                        <th>Rejected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={5} className="text-center py-4">
                            <Spinner animation="border" size="sm" /> Loading trend...
                          </td>
                        </tr>
                      )}
                      {!loading && trendRows.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center py-4 text-muted">
                            No trend data available for the selected filters.
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        trendRows.map((row, index) => (
                          <tr key={`${row.period ?? row.label ?? index}`}>
                            <td>{row.period ?? row.label ?? row.date ?? row.day ?? row.week ?? "-"}</td>
                            <td>{numericValue(row.total_appointments ?? row.total).toLocaleString()}</td>
                            <td>{numericValue(row.completed).toLocaleString()}</td>
                            <td>{numericValue(row.pending).toLocaleString()}</td>
                            <td>{numericValue(row.rejected).toLocaleString()}</td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>
              </Col>

              <Col xl={6}>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 16, overflow: "hidden" }}>
                  <div
                    style={{
                      padding: "12px 14px",
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                      fontWeight: 700,
                    }}
                  >
                    Coverage and follow-ups
                  </div>
                  <div style={{ padding: 14, display: "grid", gap: 12 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
                      <InfoTile title="Coverage" value={JSON.stringify(coverageData) === "{}" ? "-" : JSON.stringify(coverageData)} />
                      <InfoTile title="Follow-ups" value={JSON.stringify(followupsData) === "{}" ? "-" : JSON.stringify(followupsData)} />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
                      <InfoTile title="Exceptions" value={JSON.stringify(exceptionsData) === "{}" ? "-" : JSON.stringify(exceptionsData)} />
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
                      The JSON blocks above are shown as a compact preview so the web view stays usable without
                      hiding backend data. The PDF export includes the same information in table form.
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="mt-3">
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 16, overflow: "hidden" }}>
                <div
                  style={{
                    padding: "12px 14px",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    fontWeight: 700,
                  }}
                >
                  Appointment details
                </div>

                <Table responsive bordered hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Date</th>
                      <th>Staff</th>
                      <th>Client</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Approval</th>
                      <th>Time</th>
                      <th>Report</th>
                      <th>Follow-up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={10} className="text-center py-4">
                          <Spinner animation="border" size="sm" /> Loading appointments...
                        </td>
                      </tr>
                    )}
                    {!loading && detailsData?.data && detailsData.data.length === 0 && (
                      <tr>
                        <td colSpan={10} className="text-center py-4 text-muted">
                          No appointments found for the selected filters.
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      normalizeArray(detailsData?.data).map((row, index) => (
                        <tr key={`${row.appointment_id ?? row.appoint_id ?? index}`}>
                          <td>{row.appointment_id ?? row.appoint_id ?? "-"}</td>
                          <td>{formatDate(row.appointment_date ?? row.appoint_appointmentdate)}</td>
                          <td>{row.staff?.name ?? row.appoint_staffname ?? row.staff_name ?? "-"}</td>
                          <td>{row.client?.name ?? row.appoint_clientname ?? "-"}</td>
                          <td>{row.location?.name ?? row.appoint_locationname ?? "-"}</td>
                          <td>
                            <span style={statusBadgeStyle(row.status ?? row.appoint_status)}>
                              {row.status ?? row.appoint_status ?? "-"}
                            </span>
                          </td>
                          <td>
                            <span style={statusBadgeStyle(row.approval_status ?? row.appointapproval_status)}>
                              {row.approval_status ?? row.appointapproval_status ?? "-"}
                            </span>
                          </td>
                          <td>{formatTimeRange(row.time_from ?? row.appoint_timefrom, row.time_to ?? row.appoint_timeto)}</td>
                          <td>{row.report_status ?? row.report_state ?? "-"}</td>
                          <td>{formatDate(row.followup_date ?? row.appoint_followupdate)}</td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                <div style={{ color: "#475569", fontSize: 13 }}>
                  Page {currentPage} of {totalPages} | Total appointments: {pagination.total.toLocaleString()}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => loadReports(Math.max(1, currentPage - 1))}
                    disabled={loading || currentPage <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => loadReports(Math.min(totalPages, currentPage + 1))}
                    disabled={loading || currentPage >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}

function InfoTile({ title, value }) {
  const displayValue = typeof value === "string" ? value : JSON.stringify(value);

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 12,
        background: "#fff",
        minHeight: 88,
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>{title}</div>
      <div
        style={{
          marginTop: 8,
          color: "#0f172a",
          fontSize: 12,
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {displayValue}
      </div>
    </div>
  );
}

export default AppointmentReport;
